
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Trash2, Flag, Hand } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PrayerRequestCardProps {
  request: {
    id: number;
    title: string;
    content: string;
    created_at: string;
  };
  likes: number;
  comments: number;
  isAdmin: boolean;
  userHasLiked: boolean;
  onLike: (id: number, e: React.MouseEvent) => void;
  onDelete: (id: number) => void;
  onReport: (id: number, e: React.MouseEvent) => void;
  onAmen?: (id: number, e: React.MouseEvent) => void;
}

export function PrayerRequestCard({
  request,
  likes,
  comments,
  isAdmin,
  userHasLiked,
  onLike,
  onDelete,
  onReport,
  onAmen
}: PrayerRequestCardProps) {
  const [hasAmened, setHasAmened] = useState(false);

  const handleAmen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHasAmened(true);
    if (onAmen) {
      onAmen(request.id, e);
    }
  };

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReport(request.id, e);
  };

  return (
    <Card className="group bg-gradient-to-br from-white via-white to-grace-light/5 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border border-grace/10 hover:border-grace/30 animate-fade-in overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-grace/5 via-transparent to-grace-dark/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardContent className="p-6 relative">
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 line-clamp-2 group-hover:text-grace-dark transition-colors">
              {request.title}
            </h3>
            <p className="text-gray-700 mb-4 line-clamp-4 leading-relaxed">
              {request.content}
            </p>
          </div>
          
          <div className="flex justify-between items-center pt-2 border-t border-grace/10">
            <span className="text-sm text-gray-500 font-medium">
              {formatDate(request.created_at)}
            </span>
            
            <div className="flex items-center gap-3">
              {/* Amen Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`${
                        hasAmened 
                          ? 'text-grace bg-grace/10 hover:bg-grace/20' 
                          : 'text-gray-500 hover:text-grace hover:bg-grace/10'
                      } transition-all duration-200 flex items-center gap-1.5 px-3 py-1.5 rounded-full`}
                      onClick={handleAmen}
                    >
                      <Hand className="w-4 h-4" />
                      <span className="text-xs font-medium">
                        {hasAmened ? 'Prayed' : 'Amen'}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>I'll pray for this request</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Like Button */}
              <Button
                variant="ghost"
                size="sm"
                className={`${
                  userHasLiked ? 'text-red-500' : 'text-gray-500'
                } hover:text-red-500 hover:bg-red-50 transition-all duration-200 flex items-center gap-1.5 px-3 py-1.5 rounded-full`}
                onClick={(e) => onLike(request.id, e)}
              >
                <Heart className="w-4 h-4" fill={userHasLiked ? "currentColor" : "none"} />
                <span className="text-xs font-medium">{likes}</span>
              </Button>
              
              {/* Comments */}
              <div className="flex items-center gap-1.5 text-gray-500 px-3 py-1.5">
                <MessageCircle className="w-4 h-4" />
                <span className="text-xs font-medium">{comments}</span>
              </div>
              
              {/* Admin Delete */}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 p-2 rounded-full"
                  onClick={() => onDelete(request.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              
              {/* Report Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-all duration-200 p-2 rounded-full"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Flag className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="max-w-md bg-white border border-grace/20 rounded-2xl shadow-2xl">
                        <AlertDialogHeader className="text-center space-y-4 pb-2">
                          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center">
                            <Flag className="w-8 h-8 text-orange-600" />
                          </div>
                          <AlertDialogTitle className="text-2xl font-semibold text-gray-900">
                            Report Prayer Request
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-600 leading-relaxed">
                            Are you sure you want to report this prayer request? Our moderators will review the content for any violations.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex gap-3 pt-4">
                          <AlertDialogCancel className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-0 rounded-xl">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleReport}
                            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                          >
                            Report
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Report this request</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
